import React, { useEffect } from 'react';
import {
  makeStyles, IconButton, InputLabel, MenuItem, Grid,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import IconedBox from 'common/components/MainWidgetDialog/IconedBox';
// React components
import DatePicker from 'components/shared/DatePicker';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import DateRangeIcon from '@material-ui/icons/DateRange';
import { i18n } from 'config/i18n';
import { localeDateFormatMap } from 'config/date-picker';

import dayjs from 'dayjs';
import {
  LOCATION_TYPE,
  RENTAL_WIDGET_FIELDS,
  ERRORS_PREFIX,
} from 'common/components/MainWidgetDialog/utils/mainWidgetConstants';
import { MainWidgetTextField } from 'common/components/MainWidgetDialog/MainWidgetTextField';
import isBetween from 'dayjs/plugin/isBetween';
import moment from 'moment';
import 'moment/locale/de';
import 'moment/locale/ar';
import 'moment/locale/ru';
import 'moment/locale/ko';
import 'moment/locale/sv';
import 'moment/locale/fr';
import 'moment/locale/es';
import 'moment/locale/it';
import 'moment/locale/pt';
import 'moment/locale/nl';
import 'moment/locale/nb';
import 'moment/locale/da';
import 'moment/locale/ja';

import 'moment/locale/en-au';
import 'moment/locale/en-ca';
import 'moment/locale/en-gb';

dayjs.extend(isBetween);

const useStyles = makeStyles((theme, isFocused) => ({
  widgetDateTimeContainer: {
    height: '100%',
    position: 'relative',
    display: 'flex',
    border: `1px solid ${theme.palette.grey[100]}`,
    '& .IconedBoxContainer': {
      height: '100%',
    },
    '& .MuiFormControl-root': {
      width: 'auto',
      '& label.MuiFormLabel-root': {
        display: 'none',
      },
    },
    '& legend': {
      width: 0,
    },
    '& .MuiInputBase-root': {
      borderBottom: 0,
      borderRadius: 0,
      height: '100%',
      padding: 0,
      '& svg': {
        transition: 'all ease 0.5s',
      },
      '&:before, :after': {
        borderColor: 'transparent',
        border: 0,
      },
      '&.Mui-focused': {
        '& fieldset': {
          border: 0,
        },
        '&::after': {
          borderColor: 'transparent',
          border: 0,
        },
      },
      '& .MuiIconButton-root': {
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(1),
        color: theme.palette.grey[600],
      },
      '& .MuiOutlinedInput-input': {
        paddingTop: theme.spacing(2.25),
        paddingBottom: theme.spacing(1),
        marginTop: theme.spacing(1),
      },
      '& fieldset': {
        border: 0,
      },

      '&:hover': {
        backgroundColor: '#fff',
        transition: 'all ease 0.5s',
        '& svg': {
          color: theme.palette.primary.main,
        },
        '& .MuiIconButton-root': {
          backgroundColor: 'transparent !important',
        },
        '&::before, ::after': {
          borderColor: 'transparent !important',
        },
        '& fieldset': {
          borderColor: 'transparent',
        },
      },
      '&.Mui-disabled': {
        backgroundColor: `${theme.palette.action.disabledBackground} !important`,
      },

      '& .MuiInputBase-input.MuiFilledInput-input': {
        paddingLeft: theme.spacing(5.5),
        color: theme.palette.secondary.dark,
        '&::placeholder': {
          opacity: 1,
          color: isFocused ? theme.palette.grey[300] : theme.palette.grey[700],
        },
        '&.MuiSelect-select': {
          padding: theme.spacing(1, 1, 1, 5.6),
        },
      },
    },
  },
  widgetInputLabel: {
    position: 'absolute',
    left: theme.spacing(6),
    fontSize: theme.typography.pxToRem(12),
    top: theme.spacing(1.25),
    color: theme.palette.grey[200],
    zIndex: 1,
  },
  selectTextfield: {
    borderLeft: `1px solid ${theme.palette.grey[100]}`,
    width: '100% !important',

    '& .MuiFormControl-root': {
      width: '100%',
    },
    '& .MuiSelect-select': {
      paddingLeft: theme.spacing(5),
      '&:focus': {
        backgroundColor: 'transparent',
      },
    },
    '& .MuiSvgIcon-root': {
      display: 'none',
    },
  },
  DateIconError: {
    color: theme.palette.error.main,
  },
}));

const WidgetDateTimeField = ({ ...props }) => {
  const {
    pickup,
    vertical,
    date,
    handleDateTimeChange,
    time,
    timeList = [],
    label,
    minDate,
    maxDate,
    shouldDisableDate,
    errors,
    bordered,
    disabled,
  } = props;
  const classes = useStyles(props);
  const getDateFieldType = () => (pickup === LOCATION_TYPE.PICKUP
    ? RENTAL_WIDGET_FIELDS.PICKUP_DATE
    : RENTAL_WIDGET_FIELDS.DROPOFF_DATE);

  const getTimeSlotsFieldType = () => (pickup === LOCATION_TYPE.PICKUP
    ? RENTAL_WIDGET_FIELDS.PICKUP_TIME_SLOT
    : RENTAL_WIDGET_FIELDS.DROPOFF_TIME_SLOT);
  useEffect(() => {
    moment.locale(i18n.language.toLowerCase());
  }, []);
  return (
    <Grid container xs={12} className={classes.widgetDateTimeContainer}>
      <Grid className="WidgetDateField" item xs={6} sm={7} lg={vertical ? 7 : 8}>
        <InputLabel className={classes.widgetInputLabel}>{label}</InputLabel>
        <DatePicker
          label={label}
          value={date ? dayjs(date).toDate() : dayjs(null)}
          variant="inline"
          adornmentPosition="start"
          disableToolbar
          autoOk
          format={localeDateFormatMap[i18n.language].toUpperCase()}
          fieldType={getDateFieldType()}
          onChange={(value) => {
            if (
              value
              && !dayjs(date).isSame(value)
              && !shouldDisableDate(pickup, value)
              && dayjs(value).isBetween(dayjs(minDate), dayjs(maxDate), 'day', '[]')
            ) {
              handleDateTimeChange(getDateFieldType(), value);
            }
          }}
          minDate={minDate && dayjs(minDate).toDate()}
          maxDate={maxDate && dayjs(maxDate).toDate()}
          bordered={vertical || bordered}
          // eslint-disable-next-line no-shadow
          shouldDisableDate={(date) => shouldDisableDate(pickup, date)}
          id={`${ERRORS_PREFIX}${getDateFieldType()}`}
          error={!!errors[`${ERRORS_PREFIX}${getDateFieldType()}`]}
          disabled={disabled}
          InputProps={{
            startAdornment: (
              <IconButton>
                <DateRangeIcon
                  className={
                    !!errors[`${ERRORS_PREFIX}${getDateFieldType()}`] && classes.DateIconError
                  }
                />
              </IconButton>
            ),
            endAdornment: props.value && (
              <IconButton
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  // clearClicked(props.name);
                }}
              >
                <CloseIcon />
              </IconButton>
            ),
          }}
        />
      </Grid>
      <Grid item xs={6} sm={5} lg={vertical ? 5 : 4}>
        <IconedBox
          Icon={AccessTimeIcon}
          size={24}
          error={!!errors[`${ERRORS_PREFIX}${getTimeSlotsFieldType()}`]}
        >
          <MainWidgetTextField
            select
            fullWidth
            bordered={vertical || bordered}
            id={`${ERRORS_PREFIX}${getTimeSlotsFieldType()}`}
            error={!!errors[`${ERRORS_PREFIX}${getTimeSlotsFieldType()}`]}
            /* helperText={
                errors[`${ERRORS_PREFIX}${getTimeSlotsFieldType()}`] || ''
              } */
            value={time || ''}
            onChange={(e) => handleDateTimeChange(getTimeSlotsFieldType(), e.target.value)}
            variant="filled"
            disabled={disabled}
            SelectMainWidgetTextField
            className={classes.selectTextfield}
          >
            {timeList.map((option) => (
              <MenuItem key={option} value={option} sx={{ textTransform: 'uppercase' }}>
                {option}
              </MenuItem>
            ))}
          </MainWidgetTextField>
        </IconedBox>
      </Grid>
    </Grid>
  );
};
export default WidgetDateTimeField;
